import {Component, OnInit, ViewChild} from '@angular/core';
import {MatTableDataSource} from '@angular/material/table';
import {MatSort} from '@angular/material/sort';
import {FieldType, MemberAddeditComponent, MemberAddeditDialogModel} from '../../brokeragency/member-addedit/member-addedit.component';
import { MatDialog } from '@angular/material/dialog';
import { MatSnackBar } from '@angular/material/snack-bar';
import {
  ThatchCredentialMode,
  ThatchVendorMutation,
  VendorRequiredFields,
  VendorsService,
} from '../../../services/vendors/vendors.service';
import { UiAlertService } from '../../../services/ui-alert/ui-alert.service';
import { HttpErrorResponse } from '@angular/common/http';
import { SpinnerService } from '../../../services/spinner/spinner.service';


@Component({
  selector: 'app-vendors-page',
  templateUrl: './vendors-page.component.html',
  styleUrls: ['./vendors-page.component.scss']
})
export class VendorsPageComponent implements OnInit {
  @ViewChild(MatSort) sort!: MatSort;

  protected allColumnNames = ['vendorSlug', 'adapterType', 'credentialConfiguration', 'requiredFields', 'actionmenu'];
  protected items = new MatTableDataSource<any>();

  private addEditFields = [
    {name: 'vendorSlug', label: 'Vendor slug', fieldType: FieldType.F_STRING, required: true},
    {
      name: 'adapterType',
      label: 'Adapter Type',
      fieldType: FieldType.F_DROPDOWN,
      required: true,
      values: [
        'CSV (No Code):CSV',
        '834 (No Code):834',
        'Custom Driver:CUSTOM',
      ]
    },
    {
      name: 'adapterDriverKey',
      label: 'Custom Driver',
      fieldType: FieldType.F_DROPDOWN,
      required: false,
      values: [],
      invisible: (form: any) => form?.get('adapterType')?.value !== 'CUSTOM',
    },
    {
      name: 'adapterConfig',
      label: 'Adapter Config JSON',
      fieldType: FieldType.F_JSON,
      required: false,
      invisible: (form: any) => !['CSV', '834', 'CUSTOM'].includes(form?.get('adapterType')?.value),
    },
    {name: 'config', label: 'Config JSON', fieldType: FieldType.F_JSON, required: false},
    {name: 'metadata', label: 'Metadata JSON', fieldType: FieldType.F_JSON, required: false},
    {name: 'schedules', label: 'Schedules JSON', fieldType: FieldType.F_JSON, required: false},
  ];

  private readonly thatchEditFields = [
    {name: 'launchUrl', label: 'Launch URL', fieldType: FieldType.F_STRING, required: false},
    {name: 'apiUrl', label: 'API URL', fieldType: FieldType.F_STRING, required: true},
  ];

  constructor(
    private dialog: MatDialog,
    private snackBar: MatSnackBar,
    private spinnerService: SpinnerService,
    private uiAlertService: UiAlertService,
    private vendorsService: VendorsService,
  ) {}

  ngOnInit(): void {
    this.loadAdapterOptions();
    this.reloadItems();
  }

  addItem(baseData?: Record<string, any>): void {
    if (this.isThatchVendor(baseData)) {
      this.snackBar.open('Thatch credential authority cannot be cloned', 'Close', {
        duration: 4000
      });
      return;
    }
    const sourceVendorData = baseData ? this.toPlainVendor(baseData) : this.getNewVendorDefaults();
    const dialogVendorData = this.toDialogVendorData(sourceVendorData);
    const dialogRef = this.dialog.open(MemberAddeditComponent, {
      data: new MemberAddeditDialogModel(dialogVendorData, this.addEditFields, 'Vendor Config', true, 'Add')
    });
    dialogRef.afterClosed().subscribe(dialogResult => {
      if (dialogResult) {
        const vendorPayload = this.buildVendorPayload(dialogResult, sourceVendorData);
        if (!vendorPayload) {
          return;
        }
        delete vendorPayload.id;

        this.vendorsService.createVendor(vendorPayload).then(() => {
          this.reloadItems();
          this.snackBar.open('Vendor config created', 'Close', {
            duration: 3000
          });
        }).catch(() => {
          this.snackBar.open('Unable to create vendor configuration', 'Close', {
            duration: 4000
          });
        });
      }
    });
  }

  deleteItem(item: any): void {
    if (this.isThatchVendor(item)) {
      return;
    }
    this.uiAlertService.presentAlertConfirm('Are you sure you want to delete this vendor config?').then(async confirm => {
      if (confirm) {
        this.spinnerService.show('Deleting vendor config...');
        this.vendorsService.deleteVendor(item.id).then(() => {
          this.reloadItems();
          this.spinnerService.hide();
          this.snackBar.open('Vendor config deleted', 'Close', {
            duration: 3000
          });
        }).catch((err: HttpErrorResponse) => {
          this.spinnerService.hide();
          this.snackBar.open('Unable to delete vendor config', 'Close', {
            duration: 3000
          });
        });
      }
    });
  }

  async runScheduleNow(item: any, method: 'enroll' | 'payment'): Promise<void> {
    const scheduleLabel = method === 'payment' ? 'payment' : 'enroll';
    const confirm = await this.uiAlertService.presentAlertConfirm(
      `Run the ${scheduleLabel} schedule now for ${item.vendorSlug}?`
    );
    if (!confirm) {
      return;
    }

    this.spinnerService.show(`Running ${scheduleLabel} schedule...`);
    try {
      await this.vendorsService.runVendorScheduleNow(item.id, method);
      this.snackBar.open(`Vendor ${scheduleLabel} schedule completed`, 'Close', {
        duration: 3000
      });
    } catch (err) {
      this.snackBar.open(`Unable to run vendor ${scheduleLabel} schedule`, 'Close', {
        duration: 3000
      });
    } finally {
      this.spinnerService.hide();
    }
  }

  async rerunCarrierIntegration(item: any): Promise<void> {
    const vendorLabel = item?.vendorSlug || item?.id || 'this vendor';
    const confirm = await this.uiAlertService.presentAlertConfirm(
      `Rerun carrier integration for ${vendorLabel} across all associated vendor products?`
    );
    if (!confirm) {
      return;
    }

    this.spinnerService.show('Queueing carrier integration rerun...');
    try {
      const result = await this.vendorsService.rerunCarrierIntegration(
        item.id,
        'Admin requested from Vendors page',
      );
      const productCount = result?.vendorProductCount;
      const productText = typeof productCount === 'number'
        ? ` for ${productCount} vendor product${productCount === 1 ? '' : 's'}`
        : '';
      this.snackBar.open(`Carrier integration rerun queued${productText}`, 'Close', {
        duration: 3000
      });
    } catch (err) {
      this.snackBar.open('Unable to queue carrier integration rerun', 'Close', {
        duration: 3000
      });
    } finally {
      this.spinnerService.hide();
    }
  }

  editItem(item: any): void {
    if (!this.canEditVendor(item)) {
      return;
    }
    if (this.isThatchVendor(item)) {
      this.editThatchItem(item);
      return;
    }
    const vendorData = this.toPlainVendor(item);
    const dialogRef = this.dialog.open(MemberAddeditComponent, {
      data: new MemberAddeditDialogModel(this.toDialogVendorData(vendorData), this.addEditFields, 'Vendor Config')
    });
    dialogRef.afterClosed().subscribe(dialogResult => {
      if (dialogResult) {
        const vendorPayload = this.buildVendorPayload(dialogResult, vendorData);
        if (!vendorPayload) {
          return;
        }

        this.vendorsService.updateVendor(item.id, vendorPayload).then(() => {
          this.reloadItems();
          this.snackBar.open('Vendor config updated', 'Close', {
            duration: 3000
          });
        }).catch(() => {
          this.snackBar.open('Unable to update vendor configuration', 'Close', {
            duration: 4000
          });
        });
      }
    });
  }

  protected getAdapterTypeLabel(item: any): string {
    const adapter = item?.adapter || item?.metadata?.adapter;
    const adapterType = this.normalizeAdapterType(adapter?.type, item);
    if (adapterType === 'CUSTOM') {
      return `CUSTOM${adapter?.driverKey ? ` (${adapter.driverKey})` : ''}`;
    }
    return adapterType;
  }

  protected getCredentialStatusLabel(item: any): string {
    if (!this.isThatchVendor(item)) {
      return 'Not applicable';
    }
    const labels: Record<ThatchCredentialMode, string> = {
      legacy: 'Legacy database key configured',
      reference: 'Secret reference configured',
      missing: 'Credential missing',
      invalid: 'Credential configuration invalid',
    };
    const mode = item?.credentialConfiguration?.mode as ThatchCredentialMode;
    return labels[mode] || labels.invalid;
  }

  protected canCloneVendor(item: any): boolean {
    return !this.isThatchVendor(item);
  }

  protected canEditVendor(item: any): boolean {
    if (!this.isThatchVendor(item)) {
      return true;
    }
    const credentialConfiguration = item?.credentialConfiguration;
    return this.isExactThatchVendor(item)
      && credentialConfiguration?.type === 'thatch'
      && ['legacy', 'reference', 'missing'].includes(credentialConfiguration?.mode);
  }

  protected canDeleteVendor(item: any): boolean {
    return !this.isThatchVendor(item);
  }

  private isThatchVendor(item: any): boolean {
    return typeof item?.vendorSlug === 'string'
      && item.vendorSlug.trim().toLowerCase() === 'thatch';
  }

  private isExactThatchVendor(item: any): boolean {
    return item?.vendorSlug === 'thatch';
  }

  private editThatchItem(item: any): void {
    const dialogRef = this.dialog.open(MemberAddeditComponent, {
      data: new MemberAddeditDialogModel({
        launchUrl: item?.config?.launchUrl || '',
        apiUrl: item?.config?.credentials?.apiUrl || '',
      }, this.thatchEditFields, 'Thatch Non-Secret Configuration')
    });
    dialogRef.afterClosed().subscribe(dialogResult => {
      if (!dialogResult) return;
      const payload = this.buildThatchVendorPayload(dialogResult);
      if (!payload) return;
      this.vendorsService.updateThatchVendor(item.id, payload).then(() => {
        this.reloadItems();
        this.snackBar.open('Thatch vendor configuration updated', 'Close', {
          duration: 3000
        });
      }).catch(() => {
        this.snackBar.open('Unable to update Thatch vendor configuration', 'Close', {
          duration: 4000
        });
      });
    });
  }

  private buildThatchVendorPayload(dialogResult: Record<string, any>): ThatchVendorMutation | null {
    const apiUrl = typeof dialogResult?.apiUrl === 'string' ? dialogResult.apiUrl.trim() : '';
    const launchUrl = typeof dialogResult?.launchUrl === 'string' ? dialogResult.launchUrl.trim() : '';
    if (!apiUrl) {
      this.snackBar.open('Thatch API URL is required', 'Close', { duration: 4000 });
      return null;
    }
    return {
      vendorSlug: 'thatch',
      config: {
        ...(launchUrl ? { launchUrl } : {}),
        credentials: { apiUrl },
      },
    };
  }

  protected getRequiredFieldSections(item: any): Array<{label: string; values: string[]}> {
    const requiredFields = item?.requiredFields;
    if (!requiredFields) {
      return [];
    }

    return [
      { label: 'User', values: requiredFields.requiredUserFields },
      { label: 'Primary', values: requiredFields.requiredPrimaryFields },
      { label: 'Dependent', values: requiredFields.requiredDependentFields },
      { label: 'Values', values: requiredFields.requiredValues },
      { label: 'Form values', values: requiredFields.requiredFormValues },
      { label: 'Plan values', values: requiredFields.requiredPlanValues },
    ]
      .map(section => ({
        label: section.label,
        values: this.normalizeRequiredFieldValues(section.values),
      }))
      .filter(section => section.values.length);
  }

  protected hasVendorSchedule(item: any, method: 'enroll' | 'payment'): boolean {
    const scheduleField = method === 'payment' ? 'paymentFtp' : 'enrollFtp';
    return !!this.getScheduleText(item?.schedules?.[scheduleField]);
  }

  private loadAdapterOptions(): void {
    this.vendorsService.getVendorAdapterOptions().then(adapterOptions => {
      const customDriverField = this.addEditFields.find(field => field.name === 'adapterDriverKey');
      if (customDriverField) {
        customDriverField.values = this.toCustomDriverDropdownValues(adapterOptions?.customDrivers);
      }
    }).catch(() => {
      this.snackBar.open('Unable to load vendor adapter options', 'Close', {
        duration: 3000
      });
    });
  }

  private toPlainVendor(item: any): Record<string, any> {
    const vendorData = item?.plain && typeof item.plain === 'function'
      ? this.clonePlainData(item.plain())
      : this.clonePlainData(item);
    delete vendorData?.requiredFields;
    delete vendorData?.credentialConfiguration;
    return vendorData;
  }

  private getNewVendorDefaults(): Record<string, any> {
    return {
      adapter: {
        type: 'CSV',
        config: {
          effectiveDateMode: 'firstnextmonth',
          planName: 'PLAN',
          csvHeader: true,
          validation: {
            requiredUserFields: ['firstName', 'lastName', 'email'],
          },
          records: {
            primary: {
              firstName: { from: 'user.firstName' },
              lastName: { from: 'user.lastName' },
              email: { from: 'user.email' },
            },
          },
        },
      },
    };
  }

  private toDialogVendorData(vendorData: Record<string, any>): Record<string, any> {
    const dialogData = this.clonePlainData(vendorData || {});
    const adapter = dialogData.adapter || dialogData.metadata?.adapter;
    if (adapter) {
      dialogData.adapterType = this.normalizeAdapterType(adapter.type, dialogData);
      dialogData.adapterDriverKey = adapter.driverKey || null;
      dialogData.adapterConfig = adapter.config ?? null;
    } else if (!dialogData.adapterType) {
      dialogData.adapterType = this.normalizeAdapterType(null, dialogData);
      dialogData.adapterConfig = dialogData.adapterType === 'CUSTOM'
        ? null
        : this.getNewVendorDefaults().adapter.config;
    }
    return dialogData;
  }

  private buildVendorPayload(dialogResult: Record<string, any>, baseVendorData?: Record<string, any>): Record<string, any> | null {
    const payload = this.clonePlainData(baseVendorData || {});
    for (const key of Object.keys(dialogResult)) {
      payload[key] = dialogResult[key];
    }

    const adapterType = this.normalizeAdapterType(payload.adapterType, payload);
    const adapterDriverKey = payload.adapterDriverKey;
    const adapterConfig = payload.adapterConfig;

    delete payload.adapterType;
    delete payload.adapterDriverKey;
    delete payload.adapterConfig;

    payload.vendorSlug = payload.vendorSlug?.trim?.() || payload.vendorSlug;
    if (!payload.vendorSlug) {
      this.snackBar.open('Vendor slug is required', 'Close', {
        duration: 4000
      });
      return null;
    }

    if (!adapterType) {
      this.snackBar.open('Adapter type is required', 'Close', {
        duration: 4000
      });
      return null;
    }

    if (adapterType === 'CUSTOM') {
      payload.adapter = {
        type: 'CUSTOM',
        ...(adapterDriverKey ? {driverKey: adapterDriverKey} : {}),
        ...(adapterConfig != null ? {config: adapterConfig} : {}),
      };
    } else if (adapterType === 'CSV' || adapterType === '834') {
      if (!this.isObjectLike(adapterConfig)) {
        this.snackBar.open('Adapter config JSON is required for CSV/834 adapters', 'Close', {
          duration: 4000
        });
        return null;
      }
      if (!this.hasPrimaryRecordMapping(adapterConfig)) {
        this.snackBar.open('CSV/834 adapters require adapter.config.records.primary', 'Close', {
          duration: 5000
        });
        return null;
      }
      payload.adapter = {
        type: adapterType,
        config: adapterConfig,
      };
    } else {
      this.snackBar.open(`Unsupported adapter type: ${adapterType}`, 'Close', {
        duration: 4000
      });
      return null;
    }

    if (this.isObjectLike(payload.metadata)) {
      payload.metadata = {
        ...payload.metadata,
        adapter: payload.adapter,
      };
    }

    return payload;
  }

  private clonePlainData<T>(value: T): T {
    if (Array.isArray(value)) {
      return value.map(item => this.clonePlainData(item)) as T;
    }

    if (value && typeof value === 'object') {
      return Object.entries(value as Record<string, any>).reduce((result, [key, nestedValue]) => {
        if (typeof nestedValue !== 'function' && typeof nestedValue !== 'symbol' && nestedValue !== undefined) {
          result[key] = this.clonePlainData(nestedValue);
        }
        return result;
      }, {} as Record<string, any>) as T;
    }

    return value;
  }

  private hasPrimaryRecordMapping(adapterConfig: any): boolean {
    return !!adapterConfig?.records?.primary;
  }

  private isObjectLike(value: any): boolean {
    return !!value && typeof value === 'object' && !Array.isArray(value);
  }

  private getScheduleText(value: any): string {
    if (typeof value === 'string') {
      return value.trim();
    }

    if (value && typeof value === 'object' && !Array.isArray(value) && typeof value.schedule === 'string') {
      return value.schedule.trim();
    }

    return '';
  }

  private normalizeAdapterType(adapterType: any, vendorData?: Record<string, any>): 'CSV' | '834' | 'CUSTOM' {
    const normalizedAdapterType = typeof adapterType === 'string'
      ? adapterType.trim().toUpperCase()
      : '';

    if (normalizedAdapterType === 'CSV' || normalizedAdapterType === '834' || normalizedAdapterType === 'CUSTOM') {
      return normalizedAdapterType;
    }

    // Legacy vendors without adapter metadata were effectively custom drivers.
    if (!vendorData?.adapter && !vendorData?.metadata?.adapter) {
      return 'CUSTOM';
    }

    return 'CUSTOM';
  }

  private toCustomDriverDropdownValues(customDrivers: any): string[] {
    if (Array.isArray(customDrivers)) {
      return customDrivers
        .map(driver => {
          if (typeof driver === 'string') {
            return driver;
          }
          if (driver && typeof driver === 'object') {
            const value = driver.driverKey || driver.key || driver.value || driver.name;
            if (!value) {
              return null;
            }
            const label = driver.label || driver.name || value;
            return label === value ? `${value}` : `${label}:${value}`;
          }
          return null;
        })
        .filter((value): value is string => !!value)
        .sort((left: string, right: string) => left.localeCompare(right));
    }

    if (customDrivers && typeof customDrivers === 'object') {
      return Object.entries(customDrivers)
        .map(([key, driverConfig]: [string, any]) => {
          const label = driverConfig?.label || key;
          return label === key ? key : `${label}:${key}`;
        })
        .sort();
    }

    return [];
  }

  private reloadItems(): void {
    Promise.all([
      this.vendorsService.getAllVendors(),
      this.vendorsService.getRequiredFields().catch(() => {
        this.snackBar.open('Unable to load vendor required fields', 'Close', {
          duration: 3000
        });
        return [] as VendorRequiredFields[];
      }),
    ]).then(([vendors, requiredFields]) => {
      const requiredFieldsByVendor = this.buildRequiredFieldsLookup(requiredFields);
      // Default to alphabetical sort
      this.items.data = vendors
        .map(vendor => {
          (vendor as any).requiredFields = requiredFieldsByVendor.get(vendor.id) || requiredFieldsByVendor.get(vendor.vendorSlug) || null;
          return vendor;
        })
        .sort((vendor1, vendor2) => vendor1.vendorSlug > vendor2.vendorSlug ? 1 : -1);
      this.items.sort = this.sort;
    });
  }

  private buildRequiredFieldsLookup(requiredFields: VendorRequiredFields[]): Map<string, VendorRequiredFields> {
    const lookup = new Map<string, VendorRequiredFields>();
    for (const item of requiredFields || []) {
      if (item?.id) {
        lookup.set(item.id, item);
      }
      if (item?.vendorSlug) {
        lookup.set(item.vendorSlug, item);
      }
    }
    return lookup;
  }

  private normalizeRequiredFieldValues(values: any): string[] {
    return Array.isArray(values)
      ? values.map(value => `${value}`.trim()).filter(value => !!value)
      : [];
  }
}
